import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  FormControl,
  FormLabel,
  Icon,
  IconButton,
  InputGroup,
  MenuItem,
  Spinner,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { Stack } from '@chakra-ui/layout';
import { useForm } from 'react-hook-form';
import {
  Datepicker,
  Heading,
  Input,
  Select,
  Table,
  Textarea,
} from '~/components';
import { GeneralInformation } from '~/pages/Portal/Sinisters/GeneralInformation';
import {
  useDebitNoteConfiguration,
  useSinister,
} from '~/hooks/useSinisters/useSinisters';
import apiSinister from '~/utils/apiSinister';
import { queryClient } from '~/utils/react-query';

export const SinistersCloseForm: React.FC<{
  sinisterId: number;
  link?: boolean;
}> = ({ sinisterId, link = false }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const btnRef = useRef(null);
  const toast = useToast();
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    setError,
    formState,
    clearErrors,
    control,
  } = useForm();
  const { errors } = formState;
  const [isFetching, setFetching] = useState(false);
  ('');
  const [observacao, setObservacao] = useState<string>('');
  const [data_entrega, setDataEntrega] = useState<Date | undefined>();
  const { data, isLoading, refetch } = useSinister(sinisterId);
  const { data: configuration } = useDebitNoteConfiguration();

  useEffect(() => {
    if (link) handleOpen();
  }, []);

  const handleOpen = () => {
    onOpen();
  };

  const handleClose = () => {
    clearValues();
    onClose();
  };
  const clearValues = () => {
    setDataEntrega(undefined);
    setObservacao('');
    clearErrors();
  };

  async function handleCreate() {
    clearErrors();

    !data_entrega &&
      setError('data_entrega', {
        type: 'required',
        message: 'Data de entrega é obrigatória',
      });

    const { _formState } = control;
    if (!_formState.isValid) return;

    const form = observacao
      ? {
          data_de_entrega: data_entrega?.toISOString().substring(0, 10),
          comentario_da_entrega: observacao,
        }
      : {
          data_de_entrega: data_entrega?.toISOString().substring(0, 10),
        };

    setFetching(true);

    const request = apiSinister.put(`encerrar-sinistro/${sinisterId}/`, form);

    request
      .then(() => {
        toast({
          title: `Sinistro encerrado`,
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        clearValues();
        onClose();
        queryClient.invalidateQueries('sinistersList');
        queryClient.invalidateQueries(['sinisterLog', sinisterId]);
        queryClient.invalidateQueries(['sinister', sinisterId]);
        queryClient.invalidateQueries('sinisterDashboard');
      })
      .catch(error => {
        if (error.response.status === 403) {
          toast({
            title: 'Usuário sem permissão',
            description: 'Sem permissão para execução da operação.',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        } else if (error.response.status === 413) {
          toast({
            title: `Tamanho de requisição excedida`,
            description: 'Limite máximo para tamanho de arquivos é de 5MB',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        } else if (error.response.status === 400) {
          // erros de valid

          const errorsValidate = Object.entries(error.response.data);

          errorsValidate.forEach(err => {
            console.log(err[0]);
            toast({
              title: `Erros ao encerrar sinistro`,
              description: String(err[1]),
              status: 'error',
              duration: 3000,
              isClosable: true,
              position: 'top-right',
            });
          });
        } else {
          toast({
            title: `Erro ao encerrar sinistro`,
            description: 'Tente novamente',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        }
      })
      .finally(() => {
        setFetching(false);
      });
  }

  function handleDataEntrega(e: any) {
    setDataEntrega(e);
    clearErrors('data_entrega');
  }

  function handleObservacao(e: any) {
    setObservacao(e.target.value);
    if (e.target.value.length <= 255) {
      clearErrors('observacao');
    }
  }

  return (
    <>
      <MenuItem onClick={handleOpen}>Encerrar sinistro</MenuItem>
      <Drawer
        isOpen={isOpen}
        placement="right"
        onClose={handleClose}
        size="lg"
        closeOnOverlayClick={!isFetching}
      >
        <DrawerOverlay />
        <DrawerContent borderTopLeftRadius="md">
          <DrawerCloseButton isDisabled={isFetching} />
          <DrawerHeader>Encerrar sinistro</DrawerHeader>
          <DrawerBody>
            <Stack gridGap={6} bg="white" maxW={600} w="100%">
              <Stack gridGap={4}>
                <GeneralInformation sinister={data!} isAdmin />
                <FormControl
                  isInvalid={errors.data_entrega}
                  marginRight="10px"
                  width="60%"
                >
                  <FormLabel
                    htmlFor="writeUpFile"
                    style={{ fontSize: '14px' }}
                    justifyContent="space-between"
                    display="flex"
                  >
                    Data de entrega *
                  </FormLabel>
                  <Datepicker
                    onChange={e => handleDataEntrega(e)}
                    selectedDate={data_entrega}
                    isClearable={!isFetching}
                    isDisabled={isFetching}
                    error={errors?.data_entrega}
                  />
                </FormControl>
                <FormControl isInvalid={errors.observacao}>
                  <FormLabel
                    htmlFor="writeUpFile"
                    style={{ fontSize: '14px' }}
                    justifyContent="space-between"
                    display="flex"
                  >
                    Observação
                  </FormLabel>
                  <Textarea
                    name="observacao"
                    resize="none"
                    error={errors?.observacao}
                    value={observacao}
                    onChange={e => handleObservacao(e)}
                    fontSize="15px"
                    placeholder="Entre com a observação"
                  />
                </FormControl>
              </Stack>
            </Stack>
            <Button
              type="submit"
              onClick={handleSubmit(handleCreate)}
              color="white"
              background="gray.900"
              _hover={{
                bgColor: 'black',
              }}
              style={{ marginTop: '20px' }}
              isDisabled={isFetching}
            >
              Confirmar{' '}
              {isFetching && <Spinner style={{ marginLeft: '10px' }} />}
            </Button>
            <Button
              type="submit"
              onClick={handleClose}
              color="black"
              background="transparent"
              style={{
                marginTop: '20px',
                marginLeft: '10px',
              }}
              isDisabled={isFetching}
            >
              Cancelar
            </Button>
            <Text
              style={{ fontSize: '12px', color: 'gray.500', marginTop: '20px' }}
            >
              * Campos obrigatórios
            </Text>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
};
